import { FormRow, InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Select } from '@creditornot/cb-select';
import React, { useCallback, useContext, useMemo } from 'react';
import { Field, useForm, useFormState, useField } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import InputNumber from 'common/components/InputNumber';
import { useLocaleUtils } from 'common/hooks';

import FeedInputContainer from './FeedInputContainer';
import { BasicDetails, FormValues } from './types';
import { ItemDisplayUnit } from '../../../../../apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper } from '../../../../../common/components';
import { MenuPermissionsContext } from '../../../components/MenuPermissionsContext';

type Props = {
  currency: Currency;
  isFieldSynced: (field: string) => boolean;
  weightType: 'pre-packaged' | 'weighted-items';
};

type DisplayUnitFieldProps = {
  currency: Currency;
  isSynced: boolean;
  weightType: 'pre-packaged' | 'weighted-items';
  hasContentEditPermission: boolean;
};

const SharedUnitDetailsFieldStyle = css`
  & + & {
    margin-block-start: 8px;
    border-block-start: none;
  }
  ${typographyCss.Body3()};
  label {
    color: ${cbSemanticCssVars.textSecondary};
  }
`;

const UnitDetailsFieldWrapper = styled(InputContainer)`
  ${SharedUnitDetailsFieldStyle}
`;

const UnitDetailsRequiredFieldWrapper = styled(FormRow)`
  ${SharedUnitDetailsFieldStyle}
  > :first-child {
    margin-block-end: 8px;
    width: 300px;
  }
  flex-direction: column;
  padding: 0;
`;

const StyledFeedInputContainer = styled(FeedInputContainer)`
  max-width: 392px;
`;

const StyledInputContainer = styled(InputContainer)`
  max-width: 392px;
`;

const isWeightDisplayUnit = (unit: ItemDisplayUnit | null): boolean => unit === 'weight' || unit === 'weight_100g';

const isVolumeDisplayUnit = (unit: ItemDisplayUnit | null): boolean => unit === 'volume' || unit === 'volume_100ml';

const DisplayUnitField = ({ isSynced, hasContentEditPermission, weightType, currency }: DisplayUnitFieldProps) => {
  const intl = useIntl();
  const { values } = useFormState<FormValues>();
  const { priceFormatterPerUnit, getCurrencySymbol, getLocalizedUnit } = useLocaleUtils();

  const unitPriceOptions = useMemo(() => {
    const currencySymbol = getCurrencySymbol(currency);
    if (weightType === 'weighted-items') {
      return [
        {
          value: 'weight',
          label: `${intl.formatMessage({
            id: 'menu-item.weight',
          })} (${currencySymbol}/${getLocalizedUnit('kilogram')})`,
        },
        {
          value: 'weight_100g',
          label: `${intl.formatMessage({
            id: 'menu-item.weight',
          })} (${currencySymbol}/${intl.formatNumber(100)} ${getLocalizedUnit('gram')})`,
        },
      ];
    }

    return [
      {
        value: 'weight',
        label: `${intl.formatMessage({
          id: 'menu-item.weight',
        })} (${currencySymbol}/${getLocalizedUnit('kilogram')})`,
        isDisabled: !values.weightInGrams,
      },
      {
        value: 'weight_100g',
        label: `${intl.formatMessage({
          id: 'menu-item.weight',
        })} (${currencySymbol}/${intl.formatNumber(100)} ${getLocalizedUnit('gram')})`,
        isDisabled: !values.weightInGrams,
      },
      {
        value: 'volume',
        label: `${intl.formatMessage({
          id: 'menu-item.volume',
        })} (${currencySymbol}/${getLocalizedUnit('liter')})`,
        isDisabled: !values.volumeInMl,
      },
      {
        value: 'volume_100ml',
        label: `${intl.formatMessage({
          id: 'menu-item.volume',
        })} (${currencySymbol}/${intl.formatNumber(100)} ${getLocalizedUnit('milliliter')})`,
        isDisabled: !values.volumeInMl,
      },
      {
        value: 'units',
        label: `${intl.formatMessage({
          id: 'menu-item.quantity',
        })} (${currencySymbol}/${intl.formatMessage({
          id: 'menu-item.display-unit-per-pcs',
        })})`,
        isDisabled: !values.numberOfUnits,
      },
    ];
  }, [
    currency,
    getCurrencySymbol,
    getLocalizedUnit,
    intl,
    values.numberOfUnits,
    values.volumeInMl,
    values.weightInGrams,
    weightType,
  ]);

  const getDisplayUnitHint = useCallback(
    (displayUnit: ItemDisplayUnit | null, values: FormValues) => {
      const { price, weightInGrams, volumeInMl, numberOfUnits, gramsPerStep, sellByWeightInputType } = values;

      if (typeof price !== 'number') {
        return intl.formatMessage({ id: 'menu-item.display-unit-preview-hint' });
      }

      if (weightType === 'weighted-items') {
        if (displayUnit === 'weight' && typeof gramsPerStep === 'number') {
          return priceFormatterPerUnit(price * (gramsPerStep / 1000), currency, {
            baseUnit: 'kilogram',
            weightInGrams: gramsPerStep,
            isSoldByWeight: sellByWeightInputType === 'grams',
          });
        }
        if (displayUnit === 'weight_100g' && typeof gramsPerStep === 'number') {
          return priceFormatterPerUnit(price * (gramsPerStep / 1000), currency, {
            baseUnit: '100_gram',
            weightInGrams: gramsPerStep,
          });
        }
      }

      if (displayUnit === 'weight' && typeof weightInGrams === 'number') {
        return priceFormatterPerUnit(price, currency, { baseUnit: 'kilogram', weightInGrams });
      }
      if (displayUnit === 'weight_100g' && typeof weightInGrams === 'number') {
        return priceFormatterPerUnit(price, currency, { baseUnit: '100_gram', weightInGrams });
      }
      if (displayUnit === 'volume' && typeof volumeInMl === 'number') {
        return priceFormatterPerUnit(price, currency, { baseUnit: 'liter', volumeInMl });
      }
      if (displayUnit === 'volume_100ml' && typeof volumeInMl === 'number') {
        return priceFormatterPerUnit(price, currency, { baseUnit: '100_milliliter', volumeInMl });
      }
      if (displayUnit === 'units' && typeof numberOfUnits === 'number') {
        return priceFormatterPerUnit(price, currency, { baseUnit: 'pc', numberOfUnits });
      }

      return intl.formatMessage({ id: 'menu-item.display-unit-preview-hint' });
    },
    [currency, intl, priceFormatterPerUnit, weightType],
  );
  return (
    <Field<BasicDetails['displayUnit']> name="displayUnit">
      {field => (
        // Show error immediately when user clears weight, volume or quantity
        <RFFFieldErrorWrapper field={field} showErrorImmediately>
          {input => (
            <StyledFeedInputContainer
              name="displayUnit"
              isSynced={isSynced}
              hint={input.invalid ? undefined : getDisplayUnitHint(input.value, values)}
            >
              {({ disabled }) => (
                <Select
                  {...input}
                  disabled={!hasContentEditPermission || disabled}
                  placeholder={intl.formatMessage({ id: 'menu.placeholder-select' })}
                  options={unitPriceOptions}
                  clearable
                />
              )}
            </StyledFeedInputContainer>
          )}
        </RFFFieldErrorWrapper>
      )}
    </Field>
  );
};

const UnitDetailsFormSection = ({ isFieldSynced, currency, weightType }: Props) => {
  const intl = useIntl();
  const form = useForm<FormValues>();
  const { values } = useFormState<FormValues>();
  const menuPermissions = useContext(MenuPermissionsContext);

  const { getLocalizedUnit } = useLocaleUtils();

  const sellByWeightUnitTypeField = useField<FormValues['sellByWeightInputType']>('sellByWeightInputType');

  const sellByWeightOptions: { value: 'grams' | 'number_of_items'; label: string }[] = useMemo(
    () => [
      {
        value: 'number_of_items' as const,
        label: intl.formatMessage({ id: 'menu-item.sell-item-by.piece' }),
      },
      { value: 'grams' as const, label: intl.formatMessage({ id: 'menu-item.sell-item-by.weight' }) },
    ],
    [intl],
  );

  return weightType === 'pre-packaged' ? (
    <FormRow label={intl.formatMessage({ id: 'menu-item.unit-details' })}>
      <UnitDetailsFieldWrapper>
        <Field<BasicDetails['weightInGrams']> name="weightInGrams">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => {
                return (
                  <StyledFeedInputContainer
                    name="weightInGrams"
                    isSynced={isFieldSynced('weightInGrams')}
                    label={intl.formatMessage({ id: 'menu-item.weight' })}
                    hint={
                      isWeightDisplayUnit(values.displayUnit) &&
                      typeof values.weightInGrams === 'number' &&
                      values.weightInGrams >= 1000
                        ? `${intl.formatMessage({
                            id: 'menu-item.display-unit-preview-unit',
                          })} ${intl.formatNumber(values.weightInGrams / 1000, {
                            style: 'unit',
                            unit: 'kilogram',
                          })}`
                        : undefined
                    }
                  >
                    {({ disabled }) => (
                      <InputNumber
                        {...input}
                        disabled={!menuPermissions.contentEdit || disabled}
                        onChange={value => {
                          input.onChange(value);
                          if (!value && isWeightDisplayUnit(values.displayUnit)) {
                            form.change('displayUnit', null);
                          }
                          if (value && values.displayUnit === null) {
                            form.change('displayUnit', 'weight');
                          }
                        }}
                        suffixText={getLocalizedUnit('gram')}
                      />
                    )}
                  </StyledFeedInputContainer>
                );
              }}
            </RFFFieldErrorWrapper>
          )}
        </Field>
      </UnitDetailsFieldWrapper>
      <UnitDetailsFieldWrapper>
        <Field<BasicDetails['volumeInMl']> name="volumeInMl">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => {
                return (
                  <StyledFeedInputContainer
                    name="volumeInMl"
                    isSynced={isFieldSynced('volumeInMl')}
                    label={intl.formatMessage({ id: 'menu-item.volume' })}
                    hint={
                      isVolumeDisplayUnit(values.displayUnit) &&
                      typeof values.volumeInMl === 'number' &&
                      values.volumeInMl >= 1000
                        ? `${intl.formatMessage({
                            id: 'menu-item.display-unit-preview-unit',
                          })} ${intl.formatNumber(values.volumeInMl / 1000, { style: 'unit', unit: 'liter' })}`
                        : undefined
                    }
                  >
                    {({ disabled }) => (
                      <InputNumber
                        {...input}
                        disabled={!menuPermissions.contentEdit || disabled}
                        onChange={value => {
                          input.onChange(value);
                          if (!value && isVolumeDisplayUnit(values.displayUnit)) {
                            form.change('displayUnit', null);
                          }
                          if (value && values.displayUnit === null) {
                            form.change('displayUnit', 'volume');
                          }
                        }}
                        suffixText={getLocalizedUnit('milliliter')}
                      />
                    )}
                  </StyledFeedInputContainer>
                );
              }}
            </RFFFieldErrorWrapper>
          )}
        </Field>
      </UnitDetailsFieldWrapper>
      <UnitDetailsFieldWrapper>
        <Field<BasicDetails['numberOfUnits']> name="numberOfUnits">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => (
                <StyledFeedInputContainer
                  name="numberOfUnits"
                  isSynced={isFieldSynced('numberOfUnits')}
                  label={intl.formatMessage({ id: 'menu-item.quantity' })}
                >
                  {({ disabled }) => (
                    <InputNumber
                      {...input}
                      disabled={!menuPermissions.contentEdit || disabled}
                      onChange={value => {
                        input.onChange(value);
                        if (!value && values.displayUnit === 'units') {
                          form.change('displayUnit', null);
                        }
                        if (value && values.displayUnit === null) {
                          form.change('displayUnit', 'units');
                        }
                      }}
                      suffixText={intl.formatMessage({ id: 'menu-item.display-unit-pcs' })}
                    />
                  )}
                </StyledFeedInputContainer>
              )}
            </RFFFieldErrorWrapper>
          )}
        </Field>
      </UnitDetailsFieldWrapper>
      <UnitDetailsFieldWrapper label={intl.formatMessage({ id: 'menu-item.display-unit' })}>
        <DisplayUnitField
          isSynced={isFieldSynced('displayUnit')}
          hasContentEditPermission={menuPermissions.contentEdit}
          currency={currency}
          weightType={weightType}
        />
      </UnitDetailsFieldWrapper>
    </FormRow>
  ) : (
    <FormRow label={intl.formatMessage({ id: 'menu-item.unit-details' })}>
      <UnitDetailsRequiredFieldWrapper label={intl.formatMessage({ id: 'menu-item.sell-item-by.label' })} required>
        <RFFFieldErrorWrapper field={sellByWeightUnitTypeField}>
          {input => (
            <Select
              {...input}
              clearable
              disabled={!menuPermissions.contentEdit}
              onChange={option => {
                input.onChange(option ?? '');
              }}
              options={sellByWeightOptions}
            />
          )}
        </RFFFieldErrorWrapper>
      </UnitDetailsRequiredFieldWrapper>
      {!!sellByWeightUnitTypeField.input.value && (
        <UnitDetailsRequiredFieldWrapper
          label={
            sellByWeightUnitTypeField.input.value === 'grams'
              ? intl.formatMessage({ id: 'menu-item.grams-per-step.minimum-weight-steps' })
              : intl.formatMessage({ id: 'menu-item.grams-per-step.approximate-weight' })
          }
          required
        >
          <Field<BasicDetails['gramsPerStep']> name="gramsPerStep">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <StyledInputContainer>
                    <InputNumber
                      {...input}
                      onChange={value => {
                        input.onChange(value);
                        if (!value && isWeightDisplayUnit(values.displayUnit)) {
                          form.change('displayUnit', null);
                        }
                        if (value && values.displayUnit === null) {
                          form.change('displayUnit', 'weight');
                        }
                      }}
                      suffixText={getLocalizedUnit('gram')}
                    />
                  </StyledInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </UnitDetailsRequiredFieldWrapper>
      )}
      <UnitDetailsRequiredFieldWrapper label={intl.formatMessage({ id: 'menu-item.display-unit' })}>
        <DisplayUnitField
          isSynced={isFieldSynced('displayUnit')}
          hasContentEditPermission={menuPermissions.contentEdit}
          currency={currency}
          weightType={weightType}
        />
      </UnitDetailsRequiredFieldWrapper>
    </FormRow>
  );
};

export default UnitDetailsFormSection;
